import React, { useEffect, useState } from "react";
import { Plus, Trash2, Image as ImgIcon, Sliders, Check, X } from "lucide-react";
import api from "../../lib/api";

const fileToBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
});

const EMPTY = { image_base64: "", caption: "", day: "", image_focal_y: 50 };

/** Inline focal-y editor for an existing gallery item. */
function FocalEditor({ item, onSave, onClose }) {
    const [focalY, setFocalY] = useState(item.image_focal_y ?? 50);
    const [saving, setSaving] = useState(false);
    const persist = async () => {
        setSaving(true);
        try {
            await api.patch(`/gallery/${item.id}`, { image_focal_y: Number(focalY) });
            onSave(Number(focalY));
        } finally { setSaving(false); }
    };
    return (
        <div className="absolute inset-0 bg-black/90 backdrop-blur-sm p-3 flex flex-col z-10" data-testid={`focal-editor-${item.id}`}>
            <div className="flex-1 overflow-hidden bg-black/40 mb-2">
                <img
                    src={item.image_base64}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `50% ${focalY}%` }}
                />
            </div>
            <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={focalY}
                onChange={e => setFocalY(parseInt(e.target.value, 10))}
                className="accent-[#7FD93C] mb-2"
                data-testid={`focal-slider-${item.id}`}
            />
            <div className="flex items-center gap-2 text-[10px]">
                <span className="text-white/50">Haut</span>
                <span className="text-white/50 ml-auto">Bas</span>
                <span className="font-mono text-[#7FD93C] w-8 text-right">{focalY}%</span>
            </div>
            <div className="grid grid-cols-2 gap-1 mt-2">
                <button onClick={persist} disabled={saving} className="btn-primary py-1.5 flex items-center justify-center gap-1 text-xs" data-testid={`focal-save-${item.id}`}>
                    <Check size={12} />{saving ? "..." : "OK"}
                </button>
                <button onClick={onClose} className="btn-ghost py-1.5 flex items-center justify-center gap-1 text-xs">
                    <X size={12} />
                </button>
            </div>
        </div>
    );
}

export default function AdminGallery() {
    const [imgs, setImgs] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [editing, setEditing] = useState(null); // id being recentered

    const load = () => api.get("/gallery").then(r => setImgs(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        if (!form.image_base64) return;
        await api.post("/gallery", { ...form, image_focal_y: Number(form.image_focal_y) || 50 });
        setForm(EMPTY);
        load();
    };
    const del = async (id) => {
        if (!window.confirm("Supprimer cette photo ?")) return;
        await api.delete(`/gallery/${id}`);
        load();
    };
    const onFile = async (e) => {
        const f = e.target.files[0];
        if (!f) return;
        setForm({ ...form, image_base64: await fileToBase64(f) });
    };

    const focalY = Number(form.image_focal_y ?? 50);

    return (
        <div data-testid="admin-gallery">
            <h1 className="font-display text-4xl uppercase tracking-widest mb-6">Galerie</h1>

            <form onSubmit={save} className="glass-card p-5 grid gap-3 mb-6">
                <div className="grid sm:grid-cols-4 gap-3 items-center">
                    <label className="btn-ghost cursor-pointer flex items-center gap-2 justify-center" data-testid="gal-upload-btn">
                        <ImgIcon size={14} />{form.image_base64 ? "Changer" : "Choisir une image"}
                        <input type="file" accept="image/*" onChange={onFile} className="hidden" />
                    </label>
                    <input className="input-field" placeholder="Légende (optionnel)" value={form.caption} onChange={e => setForm({ ...form, caption: e.target.value })} data-testid="gal-caption" />
                    <input className="input-field" placeholder="Jour (ex: 2026-05-01)" value={form.day} onChange={e => setForm({ ...form, day: e.target.value })} />
                    <button type="submit" className="btn-primary justify-center" disabled={!form.image_base64}>
                        <Plus size={14} /> Ajouter
                    </button>
                </div>

                {form.image_base64 && (
                    <div className="border-t border-white/10 pt-4">
                        <div className="label-mini mb-2">Aperçu carré (recentrer verticalement)</div>
                        <div className="flex gap-4 items-start">
                            <div className="w-40 h-40 shrink-0 overflow-hidden bg-black/40">
                                <img
                                    src={form.image_base64}
                                    alt="Aperçu"
                                    className="w-full h-full object-cover"
                                    style={{ objectPosition: `50% ${focalY}%` }}
                                    data-testid="gal-preview-img"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-white/50 shrink-0 w-8 text-right">Haut</span>
                                    <input
                                        type="range" min="0" max="100" step="1"
                                        value={focalY}
                                        onChange={e => setForm({ ...form, image_focal_y: parseInt(e.target.value, 10) })}
                                        className="flex-1 accent-[#7FD93C]"
                                        data-testid="gal-focal-y"
                                    />
                                    <span className="text-xs text-white/50 shrink-0 w-8">Bas</span>
                                    <span className="font-mono text-xs text-[#7FD93C] shrink-0 w-10 text-right">{focalY}%</span>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, image_focal_y: 50 })}
                                        className="btn-ghost text-xs shrink-0"
                                    >Réinit.</button>
                                </div>
                                <div className="text-[11px] text-white/40 mt-2">
                                    Ajustez le curseur pour recentrer la partie importante (visages, action).
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </form>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {imgs.map(g => (
                    <div key={g.id} className="relative group glass-card overflow-hidden aspect-square" data-testid={`gal-item-${g.id}`}>
                        <img
                            src={g.image_base64}
                            alt={g.caption || ""}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: `50% ${g.image_focal_y ?? 50}%` }}
                        />
                        {editing === g.id && (
                            <FocalEditor
                                item={g}
                                onSave={(newY) => {
                                    setImgs(list => list.map(x => x.id === g.id ? { ...x, image_focal_y: newY } : x));
                                    setEditing(null);
                                }}
                                onClose={() => setEditing(null)}
                            />
                        )}
                        {editing !== g.id && (
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => setEditing(g.id)}
                                    className="p-1.5 bg-black/70 text-[#7FD93C] hover:bg-[#7FD93C] hover:text-black"
                                    title="Recentrer la photo"
                                    data-testid={`gal-recenter-${g.id}`}
                                ><Sliders size={12} /></button>
                                <button
                                    onClick={() => del(g.id)}
                                    className="p-1.5 bg-rose-500 text-white"
                                    title="Supprimer"
                                ><Trash2 size={12} /></button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
